import { forwardRef, useState } from 'react';
import { FileRejection, useDropzone, ErrorCode } from 'react-dropzone';
import { Delete, Attachment } from '@mui/icons-material';
import { Alert, CircularProgress, IconButton } from '@mui/material';
import { Uploads } from 'adminConstants/files';
import useStyles from './styles';
import { FileType, pluralize } from 'further-ui/utils';
import { fileTypeFromBuffer } from 'file-type';
import { useNotification } from 'hooks/ui/useNotification';

const defaultAcceptedImages = {
  [FileType.JPGImage]: ['.jpg', '.jpeg'],
  [FileType.PNGImage]: ['.png'],
  [FileType.SVGImage]: ['.svg'],
};

const defaultAcceptedFiles = {
  [FileType.PDF]: ['.pdf'],
  [FileType.CSV]: ['.csv'],
  [FileType.Excel]: ['.xls'],
  [FileType.ExcelAlt]: ['.xlsx'],
  [FileType.DOCX]: ['.docx'],
};

const numbersToWordsMap = {
  1: 'one',
  10: 'ten',
  100: 'hundred',
};

export interface UploadedFile extends File {
  preview: string;
  path?: string;
}

type Props = (
  | {
      accept: Record<string, Array<string>>;
      defaultAcceptGroup?: never;
    }
  | {
      accept?: never;
      defaultAcceptGroup: 'files' | 'images' | 'filesAndImages';
    }
) & {
  name: string;
  files: Array<{
    fileName: string;
    preview: string;
  }>;
  onRemove: (index: number) => void;
  maxFiles?: keyof typeof numbersToWordsMap;
  disabled?: boolean;
  maxFileSize?: number;
};

type PropsWithAsyncFileDrop = Props & {
  onDropFile: (files: Array<UploadedFile>) => Promise<void>;
  onDropFileSync?: never;
};

type PropsWithSyncFileDrop = Props & {
  onDropFileSync: (files: Array<UploadedFile>) => void;
  onDropFile?: never;
};

const Dropzone = forwardRef<
  HTMLDivElement,
  PropsWithAsyncFileDrop | PropsWithSyncFileDrop
>(
  (
    {
      files,
      onDropFile,
      onDropFileSync,
      name,
      accept,
      defaultAcceptGroup,
      onRemove,
      maxFiles = 1,
      disabled,
      maxFileSize = Uploads.maxFileSize,
    },
    ref,
  ) => {
    const [loading, setLoading] = useState(false);
    const { classes, cx } = useStyles({});
    const { error } = useNotification();

    let acceptedFileTypes: Record<string, Array<string>> = {};

    if (accept && Object.keys(accept).length > 0) {
      acceptedFileTypes = accept;
    } else if (defaultAcceptGroup === 'files') {
      acceptedFileTypes = defaultAcceptedFiles;
    } else if (defaultAcceptGroup === 'images') {
      acceptedFileTypes = defaultAcceptedImages;
    } else if (defaultAcceptGroup === 'filesAndImages') {
      acceptedFileTypes = {
        ...defaultAcceptedFiles,
        ...defaultAcceptedImages,
      };
    }

    const handleAsyncFileDrop = async (acceptedFiles: Array<File>) => {
      if (!onDropFile) return;
      setLoading(true);
      await onDropFile(
        acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          }),
        ),
      );
      setLoading(false);
    };

    const detectActualFileType = async (file: File) => {
      const buffer = await file.arrayBuffer();
      return await fileTypeFromBuffer(buffer);
    };

    const onDrop = async (acceptedFiles: Array<File>) => {
      // Check for WebP files first, not trusting extension
      for (const file of acceptedFiles) {
        const actualFileType = await detectActualFileType(file);

        if (actualFileType?.ext === 'webp') {
          error(
            'The uploaded file was formerly a WebP file and will not appear on PDFs. Please convert to PNG/JPG.',
          );
          return;
        }
      }

      const mappedFiles = acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        }),
      );
      const dropFunction = onDropFileSync || handleAsyncFileDrop;
      dropFunction(mappedFiles);
    };

    const formatErrorMessages = (fileRejections: Array<FileRejection>) => {
      const maxFilesError = fileRejections.find((rejectedFile) =>
        rejectedFile.errors.some(
          (error) => error.code === ErrorCode.TooManyFiles,
        ),
      );

      if (maxFilesError) {
        return `You can only upload ${numbersToWordsMap[maxFiles]} ${pluralize(
          'file',
          maxFiles,
        )}. Please check and try again.`;
      }

      const invalidTypeErrors = fileRejections.find((rejectedFile) =>
        rejectedFile.errors.some(
          (error) => error.code === ErrorCode.FileInvalidType,
        ),
      );

      if (invalidTypeErrors) {
        return `The following file types may be uploaded: ${Object.values(
          Object.values(acceptedFileTypes).flatMap((type) =>
            Array.isArray(type) ? type : [type],
          ),
        ).join(', ')}. Please check and try again.`;
      }

      const tooLargeErrors = fileRejections.find((rejectedFile) =>
        rejectedFile.errors.some(
          (error) => error.code === ErrorCode.FileTooLarge,
        ),
      );

      if (tooLargeErrors) {
        return `The file you are trying to upload is too large. The maximum file size is ${
          maxFileSize / 1024 / 1024
        }MB. Please check and try again.`;
      }

      const tooSmallErrors = fileRejections.find((rejectedFile) =>
        rejectedFile.errors.some(
          (error) => error.code === ErrorCode.FileTooSmall,
        ),
      );

      if (tooSmallErrors) {
        return `The file you are trying to upload is too small. Please check and try again.`;
      }

      return 'An error occurred while uploading the file. Please check and try again.';
    };

    const {
      getRootProps,
      getInputProps,
      isDragActive,
      isDragAccept,
      isDragReject,
      fileRejections,
    } = useDropzone({
      onDrop,
      accept: acceptedFileTypes,
      maxFiles,
      disabled: disabled || loading,
      maxSize: maxFileSize,
    });

    const rootClasses = cx({
      [classes.root]: true,
      [classes.active]: isDragActive,
      [classes.accept]: isDragAccept,
      [classes.reject]: isDragReject,
      [classes.disabled]: disabled,
    });

    const onlyImagesAccepted = Object.keys(acceptedFileTypes).every((a) =>
      a.startsWith('image/'),
    );

    const thumbs = files.map((file, index) => (
      <div key={index}>
        {acceptedFileTypes && !onlyImagesAccepted ? (
          <div className={classes.fileContent}>
            <div className={classes.fileName}>
              <Attachment className={classes.attachmentIcon} />{' '}
              <div
                className={classes.ellipsis}
                onClick={() => file.preview && window.open(file.preview)}
                role="button"
                tabIndex={0}
                onKeyUp={() => {}}
              >
                {
                  file?.fileName?.split('/')[
                    file?.fileName?.split('/')?.length - 1
                  ]
                }
              </div>
              {!disabled && (
                <div>
                  <IconButton
                    className={classes.removeIcon}
                    size="small"
                    disabled={loading}
                    onClick={() => onRemove(index)}
                  >
                    <Delete />
                  </IconButton>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className={classes.thumbParent}>
            <div className={classes.thumb}>
              <div className={classes.thumbInner}>
                <img
                  src={file.preview}
                  className={classes.previewImage}
                  alt={'Preview of the file just uploaded'}
                />
              </div>
              {!disabled && (
                <div className={classes.trashIcon}>
                  <IconButton
                    disabled={loading}
                    onClick={() => onRemove(index)}
                  >
                    <Delete />
                  </IconButton>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    ));

    return (
      <section ref={ref}>
        {!disabled && (
          <div {...getRootProps({})} className={rootClasses}>
            <input {...getInputProps()} name={name} />
            <span className={classes.dropzoneInstructions}>
              {loading ? (
                <CircularProgress />
              ) : (
                'Drag and drop a file here or click to search'
              )}
            </span>
          </div>
        )}
        {fileRejections?.length ? (
          <Alert severity="error" className={classes.fileErrors}>
            {formatErrorMessages(fileRejections)}
          </Alert>
        ) : null}
        {files?.length ? (
          <aside className={!onlyImagesAccepted ? '' : classes.thumbsContainer}>
            {thumbs}
          </aside>
        ) : (
          ''
        )}
      </section>
    );
  },
);

export default Dropzone;
